import React from "react";
import { Badge, Button, Card } from "react-bootstrap";
import {
  Download,
  ExternalLink,
  Eye,
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideoCamera,
  Presentation,
} from "lucide-react";

const typeConfig = {
  pdf: {
    label: "PDF",
    Icon: FileText,
    className: "bg-danger-subtle text-danger border-danger-subtle",
  },
  doc: {
    label: "DOC",
    Icon: FileText,
    className: "bg-primary-subtle text-primary border-primary-subtle",
  },
  docx: {
    label: "DOCX",
    Icon: FileText,
    className: "bg-primary-subtle text-primary border-primary-subtle",
  },
  video: {
    label: "Video",
    Icon: FileVideoCamera,
    className: "bg-info-subtle text-info-emphasis border-info-subtle",
  },
  mp4: {
    label: "Video",
    Icon: FileVideoCamera,
    className: "bg-info-subtle text-info-emphasis border-info-subtle",
  },
  mov: {
    label: "Video",
    Icon: FileVideoCamera,
    className: "bg-info-subtle text-info-emphasis border-info-subtle",
  },
  webm: {
    label: "Video",
    Icon: FileVideoCamera,
    className: "bg-info-subtle text-info-emphasis border-info-subtle",
  },
  png: {
    label: "Image",
    Icon: FileImage,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  jpg: {
    label: "Image",
    Icon: FileImage,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  jpeg: {
    label: "Image",
    Icon: FileImage,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  gif: {
    label: "Image",
    Icon: FileImage,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  webp: {
    label: "Image",
    Icon: FileImage,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  xls: {
    label: "Sheet",
    Icon: FileSpreadsheet,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  xlsx: {
    label: "Sheet",
    Icon: FileSpreadsheet,
    className: "bg-success-subtle text-success border-success-subtle",
  },
  ppt: {
    label: "Slides",
    Icon: Presentation,
    className: "bg-warning-subtle text-warning-emphasis border-warning-subtle",
  },
  pptx: {
    label: "Slides",
    Icon: Presentation,
    className: "bg-warning-subtle text-warning-emphasis border-warning-subtle",
  },
  zip: {
    label: "Archive",
    Icon: FileArchive,
    className: "bg-secondary-subtle text-secondary border-secondary-subtle",
  },
};

const getFileType = (material) => {
  const explicitType = String(material.type || "").toLowerCase();
  if (explicitType) return explicitType;
  return String(material.fileName || "").split(".").pop().toLowerCase();
};

const getSkillName = (skill) =>
  typeof skill === "string" ? skill : skill?.name || skill?.id || "";

const getAudienceLabel = (audience) => {
  switch (audience || "student") {
    case "tutor":
      return "Tutors";
    case "all":
      return "Both";
    default:
      return "Students";
  }
};

export default function MaterialCard({
  material,
  onView,
  showPreviewButton = true,
  showAudienceBadge = true,
}) {
  const type = getFileType(material);
  const config = typeConfig[type] || {
    label: type ? type.toUpperCase() : "File",
    Icon: File,
    className: "bg-light text-secondary border",
  };
  const Icon = config.Icon;
  const title = material.title || material.fileName || "Untitled material";
  const skills = Array.isArray(material.skills) ? material.skills : [];
  const tags = Array.isArray(material.tags) ? material.tags : [];

  return (
    <Card
      className="material-card h-100"
      style={{ boxShadow: "0 10px 24px rgba(15, 23, 42, 0.12)" }}
    >
      <Card.Body className="d-flex flex-column p-3">
        <div className="d-flex align-items-start gap-3">
          <div
            className={`d-flex align-items-center justify-content-center rounded-2 border ${config.className}`}
            style={{ width: 52, height: 52, flex: "0 0 52px" }}
          >
            <Icon size={28} strokeWidth={1.8} />
          </div>

          <div className="min-w-0 flex-grow-1">
            <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
              <Badge bg="light" text="dark" className="border fw-semibold">
                {config.label}
              </Badge>
              {showAudienceBadge && (
                <Badge bg={material.audience === "tutor" ? "warning" : material.audience === "all" ? "info" : "secondary"}>
                  {getAudienceLabel(material.audience)}
                </Badge>
              )}
            </div>
            <h6 className="mb-1 text-truncate" title={title}>
              {title}
            </h6>
            {material.fileName && (
              <div className="small text-muted text-truncate" title={material.fileName}>
                {material.fileName}
              </div>
            )}
          </div>
        </div>

        <div className="mt-3 d-flex flex-wrap gap-1">
          {skills.slice(0, 3).map((skill, index) => (
            <Badge bg="primary" key={skill.id || getSkillName(skill) || index}>
              {getSkillName(skill)}
            </Badge>
          ))}
          {skills.length > 3 && <Badge bg="light" text="dark">+{skills.length - 3}</Badge>}
          {tags.slice(0, 2).map((tag, index) => (
            <Badge bg="secondary" key={`${tag}-${index}`}>
              {tag}
            </Badge>
          ))}
        </div>

        <div className="mt-auto pt-3 d-flex gap-2">
          {showPreviewButton && (
            <Button
              size="sm"
              variant="outline-primary"
              className="d-inline-flex align-items-center gap-1"
              onClick={() => onView?.(material)}
            >
              <Eye size={15} />
              View
            </Button>
          )}
          {material.url && (
            <>
              <a
                href={material.url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-primary d-inline-flex align-items-center gap-1"
              >
                <ExternalLink size={15} />
                Open
              </a>
              <a
                href={material.url}
                download={material.fileName || title}
                className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1 ms-auto"
                title="Download"
              >
                <Download size={15} />
              </a>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
